/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { DialogTitle } from "@headlessui/react";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import { useQuery } from "@tanstack/react-query";
import Drawer from "../../../blocks/drawer";
import PlanAlert from "../../../components/PlanAlert";
import { RichTextEditor } from "../../../components/RichTextEditor";
import { fetchSegmentCustomers } from "../../../api/queries";
import usePlanTier, { tierAllows } from "../../../hooks/usePlanTier";
import { useLanguage } from "../../../contexts/LanguageContext";
import { useStore } from "../../../contexts/StoreContext";
import {
  PUSH_SEGMENTS,
  SEGMENT_COLORS,
  segmentCustomersQueryKey,
  segmentLabelKey,
} from "../../dashboard/segments";
import { formatNumber } from "../../dashboard/format";
import { timeZoneLabel } from "../../../utils/timeZones";
import { zonedWallTimeToUtcIso } from "../../../utils/zonedTime";
import {
  CHANNELS,
  SMS_UCS2_LIMIT,
  channelAccess,
  hasMessageContent,
  messageCharLimit,
  passName,
  providerLabel,
  providersFor,
} from "../messageUtils";
import MessagePreview from "./MessagePreview";
import { chipClass, inputClass, labelClass } from "../styles";
import { CloseButton } from "./ui";

const deriveAudience = (formData) => {
  if (formData.segment) return "segment";
  if (formData.pass_id && formData.pass_id !== "all") return "card";
  return "all";
};

// Lists from the integrations' phoneNumbers / emailAddresses endpoints, which
// answer either a bare array or { phone_numbers } / { email_addresses }.
const senderList = (data, key) =>
  Array.isArray(data) ? data : data?.[key] || [];

function RadioDot({ on, off }) {
  return (
    <span
      aria-hidden="true"
      className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border-2 ${
        on ? "border-indigo-600" : off ? "border-gray-300" : "border-gray-400"
      }`}
    >
      <span
        className={`h-2 w-2 rounded-full ${on ? "bg-indigo-600" : "bg-transparent"}`}
      />
    </span>
  );
}

function LockedBadge({ children }) {
  return (
    <span className="rounded-full bg-indigo-50 px-1.5 py-px text-[11px] font-semibold text-indigo-700">
      {children}
    </span>
  );
}

const MessageModal = ({
  isOpen,
  onClose,
  formData,
  setFormData,
  onSubmit,
  isEditing,
  isSaving = false,
  passes = [],
  integrations = [],
  isLoadingIntegrations = false,
  phoneNumbers,
  emailAddresses,
  isLoadingPhoneNumbers,
  isLoadingEmailAddresses,
  handleMessageTypeChange,
  // The zone the scheduled time is entered in, resolved once by
  // useMarketingData (which also fills an edit in it); fromStore picks the note.
  timeZone,
  timeZoneFromStore,
}) => {
  const { t, language } = useLanguage();
  const { selectedStore } = useStore();
  const tier = usePlanTier(selectedStore);
  const canTargetSegments = tierAllows(tier, "insights");

  const channel = formData.message_type || "push";
  const isPush = channel === "push";
  const isEmail = channel === "email";
  const isDirect = !isPush;

  // Per-session UI state. The drawer stays mounted between sessions, so it is
  // reset whenever it opens or closes, during render rather than in an
  // effect so a stale value is never painted for a frame.
  const [audience, setAudience] = useState(() => deriveAudience(formData));
  // The scheduled_at value last rejected as unreadable; its error shows only
  // while the field still holds it.
  const [rejectedScheduledAt, setRejectedScheduledAt] = useState(null);
  // "channels" | "segments" while the plan dialog is open.
  const [planLock, setPlanLock] = useState(null);
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (wasOpen !== isOpen) {
    setWasOpen(isOpen);
    setRejectedScheduledAt(null);
    setPlanLock(null);
    setAudience(deriveAudience(formData));
  }
  const scheduledAtError =
    rejectedScheduledAt !== null &&
    rejectedScheduledAt === formData.scheduled_at
      ? t("marketing.form.invalidScheduledAt")
      : "";

  const set = (patch) => setFormData((prev) => ({ ...prev, ...patch }));

  // Segment audience (push only, Growth and up). The count is an estimate:
  // the API rebuilds the segment when the push goes out (M6: only the
  // selected segment is counted).
  const segmentPassId =
    formData.pass_id && formData.pass_id !== "all" ? formData.pass_id : null;
  const segmentEstimate = useQuery({
    queryKey: segmentCustomersQueryKey(
      selectedStore?.id,
      formData.segment,
      1,
      segmentPassId,
    ),
    queryFn: () =>
      fetchSegmentCustomers(selectedStore?.id, formData.segment, {
        page: 1,
        passId: segmentPassId,
      }),
    enabled:
      isOpen &&
      isPush &&
      canTargetSegments &&
      !!formData.segment &&
      !!selectedStore?.id,
  });

  const access = channelAccess(channel, { integrations, tier });
  const providers = providersFor(channel, integrations);
  const noProvider =
    isDirect && !isLoadingIntegrations && providers.length === 0;

  const senders = isEmail
    ? senderList(emailAddresses, "email_addresses")
    : senderList(phoneNumbers, "phone_numbers");
  // An edited message keeps its sender even if the provider no longer lists it.
  const senderOptions =
    formData.from && !senders.includes(formData.from)
      ? [formData.from, ...senders]
      : senders;
  const loadingSenders = isEmail
    ? isLoadingEmailAddresses
    : isLoadingPhoneNumbers;

  const pickChannel = (type) => {
    if (type === channel) return;
    if (channelAccess(type, { integrations, tier }) === "locked") {
      setPlanLock("channels");
      return;
    }
    handleMessageTypeChange(type);
    if (type !== "push" && audience === "segment") {
      setAudience(
        formData.pass_id && formData.pass_id !== "all" ? "card" : "all",
      );
    }
  };

  const pickAudience = (next) => {
    if (next === "segment") {
      if (!isPush) return;
      if (!canTargetSegments && !formData.segment) {
        setPlanLock("segments");
        return;
      }
      setAudience("segment");
      return;
    }
    setAudience(next);
    if (next === "all") {
      set({ pass_id: "all", segment: "" });
    } else {
      set({
        segment: "",
        pass_id:
          formData.pass_id && formData.pass_id !== "all"
            ? formData.pass_id
            : String(passes[0]?.id ?? ""),
      });
    }
  };

  const handleSubmit = () => {
    let scheduledAt = null;
    if (formData.when === "later") {
      scheduledAt = zonedWallTimeToUtcIso(formData.scheduled_at, timeZone);
      // A value we can't read as a wall time must not reach the API raw — it
      // would be parsed there in some other zone, or not at all.
      if (!scheduledAt) {
        setRejectedScheduledAt(formData.scheduled_at);
        return;
      }
    }
    onSubmit({ ...formData, scheduled_at: scheduledAt });
  };

  const limit = messageCharLimit(channel, formData.message);
  const length = formData.message.length;
  const overLimit = !!limit && length > limit;
  // Why an SMS limit dropped from 160 to 70.
  const smsUnicode = channel === "sms" && limit === SMS_UCS2_LIMIT;

  const blocked =
    isSaving ||
    access !== "open" ||
    !hasMessageContent(formData.message) ||
    (audience === "segment" && !formData.segment);

  const submitText = isEditing
    ? t("marketing.form.saveChanges")
    : t(
        `marketing.form.submit.${formData.when === "later" ? "schedule" : "send"}.${channel}`,
      );

  const channelHints = {
    push: t("marketing.channels.push.hint"),
    email: t("marketing.channels.email.hint"),
    sms: t("marketing.channels.sms.hint"),
  };

  const audienceOptions = [
    {
      id: "all",
      label: t("marketing.form.audienceAll"),
      hint: t("marketing.form.audienceAllHint"),
    },
    passes.length > 0 && {
      id: "card",
      label: t("marketing.form.audienceCard"),
      hint: t("marketing.form.audienceCardHint"),
    },
    {
      id: "segment",
      label: t("marketing.form.audienceSegment"),
      hint: isPush
        ? t("marketing.form.audienceSegmentHint")
        : t("marketing.form.audienceSegmentPushOnly"),
      off: !isPush,
      locked: isPush && !canTargetSegments && !formData.segment,
    },
  ].filter(Boolean);

  const cardChips = (withAll) => (
    <div
      role="group"
      aria-label={t("marketing.form.card")}
      className="flex flex-wrap gap-1.5 pl-1"
    >
      {withAll && (
        <button
          type="button"
          aria-pressed={formData.pass_id === "all"}
          onClick={() => set({ pass_id: "all" })}
          className={chipClass(formData.pass_id === "all")}
        >
          {t("marketing.form.audienceAll")}
        </button>
      )}
      {passes.map((pass) => {
        const on = formData.pass_id === String(pass.id);
        return (
          <button
            key={pass.id}
            type="button"
            aria-pressed={on}
            onClick={() => set({ pass_id: String(pass.id) })}
            className={chipClass(on)}
          >
            {passName(pass)}
          </button>
        );
      })}
    </div>
  );

  return (
    <Drawer
      open={isOpen}
      setOpen={onClose}
      size="full"
      panelClassName="lg:max-w-[960px]"
      headerClassName="bg-white text-gray-900"
      showCloseButton={false}
      header={
        <>
          <DialogTitle as="h2" className="text-xl font-semibold text-gray-900">
            {isEditing
              ? t("marketing.form.editTitle")
              : t("marketing.page.newMessage")}
          </DialogTitle>
          <CloseButton onClick={onClose} label={t("common.close")} />
        </>
      }
      onSubmit={handleSubmit}
      onCancel={onClose}
      submitText={submitText}
      cancelText={t("common.cancel")}
      submitDisabled={blocked}
    >
      <div className="flex min-h-full flex-wrap">
        <div className="flex min-w-0 flex-[999_1_420px] flex-col gap-[18px] p-6">
          {/* Channel */}
          <div className="flex flex-col gap-2">
            <span id="marketing-channel-label" className={labelClass}>
              {t("marketing.channels.label")}
            </span>
            <div
              role="radiogroup"
              aria-labelledby="marketing-channel-label"
              className="grid grid-cols-3 gap-2"
            >
              {CHANNELS.map((type) => {
                const on = channel === type;
                const locked =
                  !on &&
                  channelAccess(type, { integrations, tier }) === "locked";
                return (
                  <button
                    key={type}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    aria-disabled={locked || undefined}
                    onClick={() => pickChannel(type)}
                    className={`flex flex-col items-start gap-1 rounded-[10px] px-3 py-2.5 text-left ${
                      on
                        ? "border-2 border-indigo-600 bg-indigo-50/60"
                        : locked
                          ? "border border-gray-200 bg-gray-50"
                          : "border border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <span className="flex w-full items-center justify-between gap-1.5">
                      <span
                        className={`text-sm font-semibold ${locked ? "text-gray-600" : "text-gray-900"}`}
                      >
                        {t(`marketing.channels.${type}.label`)}
                      </span>
                      {locked && (
                        <LockClosedIcon
                          className="h-3.5 w-3.5 text-indigo-700"
                          aria-hidden="true"
                        />
                      )}
                    </span>
                    {locked ? (
                      <LockedBadge>
                        {t("marketing.channels.locked")}
                      </LockedBadge>
                    ) : (
                      <span className="text-xs text-gray-600">
                        {channelHints[type]}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Provider, From, Subject (email and SMS) */}
          {isDirect && (
            <>
              {noProvider ? (
                <p
                  role="alert"
                  className="m-0 rounded-[10px] bg-red-50 px-3 py-2.5 text-[13px] leading-normal text-red-900"
                >
                  {isEmail
                    ? t("marketing.errors.noEmailIntegration")
                    : t("marketing.errors.noSmsIntegration")}{" "}
                  <Link
                    to={`/integrations?tag=${channel}`}
                    className="font-semibold text-red-700 underline-offset-2 hover:underline"
                  >
                    {t("marketing.errors.connectInIntegrations")}
                  </Link>
                </p>
              ) : (
                <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-3">
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="integration_id" className={labelClass}>
                      {t("marketing.form.provider")}
                    </label>
                    <select
                      id="integration_id"
                      name="integration_id"
                      value={formData.integration_id || ""}
                      onChange={(e) =>
                        set({ integration_id: e.target.value, from: "" })
                      }
                      className={inputClass}
                      required
                    >
                      <option value="">
                        {t("marketing.form.chooseProvider")}
                      </option>
                      {isLoadingIntegrations ? (
                        <option disabled>{t("common.loading")}</option>
                      ) : (
                        providers.map((integration) => (
                          <option
                            key={integration.id}
                            value={String(integration.id)}
                          >
                            {providerLabel(integration)}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  {formData.integration_id && (
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="from" className={labelClass}>
                        {t("marketing.form.from")}
                      </label>
                      <select
                        id="from"
                        name="from"
                        value={formData.from}
                        onChange={(e) => set({ from: e.target.value })}
                        className={inputClass}
                        required
                      >
                        <option value="">{t("marketing.form.select")}</option>
                        {senderOptions.map((sender) => (
                          <option key={sender} value={sender}>
                            {sender}
                          </option>
                        ))}
                      </select>
                      {loadingSenders && (
                        <span className="text-xs text-gray-500">
                          {t("common.loading")}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
              {isEmail && (
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="subject" className={labelClass}>
                    {t("marketing.form.emailSubject")}
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    autoComplete="off"
                    value={formData.subject || ""}
                    onChange={(e) => set({ subject: e.target.value })}
                    placeholder={t("marketing.form.subjectPlaceholder")}
                    className={inputClass}
                    required
                  />
                </div>
              )}
            </>
          )}

          {/* Audience */}
          <div className="flex flex-col gap-2">
            <span id="marketing-audience-label" className={labelClass}>
              {t("marketing.form.audience")}
            </span>
            <div
              role="radiogroup"
              aria-labelledby="marketing-audience-label"
              className="flex flex-col gap-2"
            >
              {audienceOptions.map((option) => {
                const on = audience === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    aria-disabled={option.off || option.locked || undefined}
                    onClick={() => pickAudience(option.id)}
                    className={`flex w-full items-center gap-3 rounded-[10px] px-3.5 py-2.5 text-left ${
                      on
                        ? "border-2 border-indigo-600 bg-indigo-50/60"
                        : option.off
                          ? "cursor-not-allowed border border-gray-200 bg-gray-50"
                          : "border border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <RadioDot on={on} off={option.off} />
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="text-sm font-semibold text-gray-900">
                        {option.label}
                      </span>
                      <span className="text-xs text-gray-600">
                        {option.hint}
                      </span>
                    </span>
                    {option.locked && (
                      <span className="flex items-center gap-1">
                        <LockClosedIcon
                          className="h-3.5 w-3.5 text-indigo-700"
                          aria-hidden="true"
                        />
                        <LockedBadge>
                          {t("marketing.form.audienceSegmentLocked")}
                        </LockedBadge>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {audience === "card" && cardChips(false)}

            {audience === "segment" && isPush && (
              <div className="flex flex-col gap-2">
                <div
                  role="group"
                  aria-label={t("marketing.form.audienceSegment")}
                  className="flex flex-wrap gap-1.5 pl-1"
                >
                  {PUSH_SEGMENTS.map((segment) => {
                    const on = formData.segment === segment;
                    return (
                      <button
                        key={segment}
                        type="button"
                        aria-pressed={on}
                        disabled={!canTargetSegments && !on}
                        onClick={() => set({ segment })}
                        className={`${chipClass(on)} disabled:cursor-not-allowed disabled:opacity-50`}
                      >
                        <span
                          aria-hidden="true"
                          className="h-2 w-2 rounded-full ring-1 ring-gray-200"
                          style={{
                            background: SEGMENT_COLORS[segment] || "#B45309",
                          }}
                        />
                        {t(segmentLabelKey(segment))}
                      </button>
                    );
                  })}
                </div>
                <p
                  className="m-0 pl-1 text-xs text-gray-600"
                  aria-live="polite"
                >
                  {!formData.segment
                    ? t("marketing.form.segmentPick")
                    : segmentEstimate.isLoading
                      ? t("marketing.form.segmentCounting")
                      : Number.isFinite(segmentEstimate.data?.total_count)
                        ? t("marketing.form.segmentEstimate", {
                            count: segmentEstimate.data.total_count,
                            value: formatNumber(
                              segmentEstimate.data.total_count,
                              language,
                            ),
                          })
                        : t("marketing.form.segmentHint")}
                </p>
                {passes.length > 1 && (
                  <div className="flex flex-col gap-1.5">
                    <span className="pl-1 text-xs font-semibold text-gray-600">
                      {t("marketing.form.card")}
                    </span>
                    {cardChips(true)}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Message */}
          <div className="flex flex-col gap-1.5">
            <div className="flex justify-between gap-2">
              {isEmail ? (
                <span className={labelClass}>
                  {t("marketing.form.message")}
                </span>
              ) : (
                <label htmlFor="message" className={labelClass}>
                  {t("marketing.form.message")}
                </label>
              )}
              {limit && (
                <span
                  className={`text-[13px] font-medium tabular-nums ${overLimit ? "text-amber-700" : "text-gray-500"}`}
                  aria-label={t("marketing.form.charCount", {
                    count: length,
                    limit,
                  })}
                >
                  {length} / {limit}
                </span>
              )}
            </div>
            {isEmail ? (
              <RichTextEditor
                content={formData.message}
                onChange={(content) => set({ message: content })}
              />
            ) : (
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={(e) => set({ message: e.target.value })}
                rows={3}
                className="block w-full resize-y rounded-[10px] border border-gray-300 px-3 py-2.5 text-sm leading-normal text-gray-900 focus:border-indigo-500 focus:ring-indigo-500"
                required
              />
            )}
            {smsUnicode && (
              <p className="m-0 text-xs text-gray-600">
                {t("marketing.form.smsUnicodeLimit", { limit })}
              </p>
            )}
            {overLimit && (
              <p className="m-0 text-xs text-amber-800">
                {t(`marketing.form.overLimit.${channel}`, { limit })}
              </p>
            )}
          </div>

          {/* When */}
          <div className="flex flex-col gap-2">
            <span id="marketing-when-label" className={labelClass}>
              {t("marketing.form.when")}
            </span>
            <div
              role="radiogroup"
              aria-labelledby="marketing-when-label"
              className="inline-flex gap-0.5 self-start rounded-[10px] bg-gray-100 p-[3px]"
            >
              {[
                ["now", t("marketing.form.sendNow")],
                ["later", t("marketing.form.schedule")],
              ].map(([value, label]) => {
                const on = (formData.when || "now") === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => set({ when: value })}
                    className={`min-h-[38px] rounded-lg px-3.5 text-[13px] font-medium ${
                      on
                        ? "bg-white text-gray-900 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {formData.when === "later" && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor="scheduled_at" className="sr-only">
                  {t("marketing.form.sendAt")}
                </label>
                <input
                  type="datetime-local"
                  id="scheduled_at"
                  name="scheduled_at"
                  value={formData.scheduled_at}
                  onChange={(e) => set({ scheduled_at: e.target.value })}
                  className={`${inputClass} w-auto self-start ${scheduledAtError ? "border-red-300" : ""}`}
                  aria-invalid={scheduledAtError ? true : undefined}
                  required
                />
                {scheduledAtError && (
                  <p className="m-0 text-sm text-red-600">{scheduledAtError}</p>
                )}
                <p className="m-0 text-xs text-gray-600">
                  {timeZoneFromStore
                    ? t("marketing.form.timeZoneNote", {
                        timeZone: timeZoneLabel(timeZone),
                      })
                    : t("marketing.form.timeZoneBrowserFallback", {
                        timeZone: timeZoneLabel(timeZone),
                      })}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Preview */}
        <section
          aria-label={t("marketing.preview.label")}
          className="flex min-w-0 flex-[1_1_300px] flex-col items-center gap-3 bg-gray-100 p-6"
        >
          <span className="self-start text-[13px] font-semibold text-gray-700">
            {t("marketing.preview.label")}
          </span>
          <MessagePreview
            formData={formData}
            storeName={selectedStore?.name || ""}
            timeZone={timeZone}
          />
        </section>
      </div>

      <PlanAlert
        isOpen={!!planLock}
        onClose={() => setPlanLock(null)}
        title={
          planLock === "channels"
            ? t("marketing.plan.channelsTitle")
            : t("common.planAlertTitle")
        }
        description={
          planLock === "channels"
            ? t("marketing.plan.channelsBody")
            : t("common.planAlertDescription", {
                plan: t("pricing.tier.growth.name"),
              })
        }
        plan={planLock === "channels" ? "Advanced" : "Growth"}
      />
    </Drawer>
  );
};

export default React.memo(MessageModal);
